import { useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import * as yup from 'yup';
import { Formik, Field } from 'formik';

import {
    FovFieldError,
    FovInput,
    FovLoader,
    FovButton,
    Tags,
    VerticalSpacer,
} from 'components';
import { getTeamData, updateTeamData } from 'store/actions/teamData';

const ButtonContainer = styled('div')`
    display: inline-flex;
    width: 100%;
    max-width: 25rem;
`;

const InputContainer = styled('div')`
    max-width: 25rem;
`;

const Settings = withTheme(() => {
    const dispatch = useDispatch();
    const formikRef = useRef();

    const teamDataStore = useSelector(state => state.teamData);

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const teamId = useMemo(() => teamDataStore.teamId, [ teamDataStore.teamId ]);
    const selectedTeamId = useMemo(() => teamDataStore.selectedTeamId, [ teamDataStore.selectedTeamId ]);
    const teamDataIsLoading = useMemo(() => teamDataStore.isLoading, [ teamDataStore.isLoading ]);
    const allowedDomains = useMemo(() => teamDataStore.allowedDomains, [ teamDataStore.allowedDomains ]);
    const displayName = useMemo(() => teamDataStore.displayName, [ teamDataStore.displayName ]);
    const domains = allowedDomains ? allowedDomains.join(',') : '';


    useEffect(() => {
        if (selectedTeamId !== teamId) {
            dispatch(getTeamData(token, selectedTeamId));
        }
    }, [ teamId, selectedTeamId ]);

    const setRef = form => {
        if (!formikRef.current) {
            formikRef.current = form;
        }
    };

    useEffect(() => {
        if (displayName) {
            formikRef.current?.setFieldValue('displayName', displayName);
        }
    }, [ displayName ]);

    const handleSubmit = useCallback(args => {
        const { values, setSubmitting } = args;
        const { displayName, domains } = values;

        const domainArr = domains.split(',');

        dispatch(updateTeamData(token, teamId, { displayName, domains: domainArr }))
            .then(() => {
                setSubmitting(false);
            })
            .catch(() => {
                setSubmitting(false);
            });

    }, [ teamId ]);

    const defaults = {
        displayName: displayName || '',
        domains: domains || '',
    };

    const schema = yup.object().shape({
        displayName: yup.string().required('A display name is required.'),
        domains: yup.string().required('At least one domain is required.')
            .test('is-valid', 'All domains must start with @',
                value => value?.split?.(',').every(domain => domain.startsWith('@')),
            ),
    });

    return (
        <FovLoader
            text='Retrieving team data'
            resource={!teamDataIsLoading}
        >
        <Formik
            initialValues={defaults}
            validationSchema={schema}
            onSubmit={(values, handlers) => {
                const { setSubmitting } = handlers;
                handleSubmit({ values, setSubmitting });
            }}
            validateOnChange={true}
        >
        {form => (
            <Fragment>
            {setRef(form)}
            Display Name
            <Field type='text' name='displayName'>
            {data => (
                <InputContainer>
                    <FovInput
                        value={data.field.value}
                        placeholder={displayName}
                        {...data.field}
                        onChange={e => {
                            const newVal = e.target.value;
                            form.setFieldValue(data.field.name, newVal);
                        }}
                    />
                </InputContainer>
            )}
            </Field>
            <FovFieldError name='displayName' />

            Domains
            <VerticalSpacer size={10} />

            <Field type='input' name='domains'>
            {data => (
                <Tags
                    values={data.field.value ? data.field.value : []}
                    newTagText='Add domain'
                    onChange={(e, domains) => {
                        form.setFieldValue(data.field.name, domains);
                    }}
                />
            )}
            </Field>
            <FovFieldError name='domains' />
            <ButtonContainer>
                <FovButton
                    type='submit'
                    disabled={form.isSubmitting}
                    onClick={form.handleSubmit}
                >
                Save Settings
                </FovButton>
            </ButtonContainer>


            </Fragment>
        )}
        </Formik>

        </FovLoader>
    );

});

export { Settings };
